import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BizouLogo } from "@/components/BizouLogo";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/LanguageContext";

export default function ConsentScreen({ user, onConsent }) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!accepted) { toast.error(t("toast.consentRequired")); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/consent`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accepted: true }),
      });
      if (!response.ok) throw new Error();
      onConsent();
    } catch {
      toast.error(t("toast.consentError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="max-w-md w-full" data-testid="consent-screen">
        <div className="text-center mb-8">
          <BizouLogo size="lg" className="justify-center" />
        </div>

        <div className="bg-white rounded-3xl border border-[#F0E8EB] shadow-lg p-8">
          <h1 className="text-2xl font-medium text-[#1C1917] mb-3 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("consent.welcome", { name: user?.name?.split(" ")[0] })}
          </h1>
          <p className="text-[#78716C] text-center mb-8 text-sm">
            {t("consent.beforeStarting")}
          </p>

          <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-[#E7E5E4] hover:border-[#E8175D] transition-colors mb-6" data-testid="consent-checkbox-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 rounded accent-[#E8175D] flex-shrink-0"
              data-testid="consent-checkbox"
            />
            <span className="text-sm text-[#1C1917] leading-relaxed">
              {t("consent.checkboxText")}{" "}
              <a href="/privacy" target="_blank" className="text-[#E8175D] hover:underline font-medium">{t("consent.privacyPolicyLink")}</a>.
            </span>
          </label>

          <Button
            onClick={handleSubmit}
            disabled={!accepted || loading}
            className="w-full h-12 rounded-full bg-[#E8175D] text-white font-medium hover:bg-[#C4134F] disabled:opacity-50"
            data-testid="consent-submit-btn"
          >
            {loading ? t("consent.processing") : t("consent.continue")}
          </Button>
        </div>
      </div>
    </div>
  );
}
